<h3 class="with-manual-link">
    <span i18n="@@latency-title">Antwortzeiten</span>
    <app-manual-link
        href="https://reflact.github.io/engine/Handbuch/Nutzeroberfläche/Bot-Verwaltung/Insights/Antwortzeiten"
    ></app-manual-link>
</h3>
<p
    class="text-muted"
    i18n="@@latency-description"
>Wie lange warten Nutzer, bis der Avatar zu sprechen beginnt? Gemessen wird bis zum tatsächlich hörbaren Ton im Browser.
</p>

<div class="d-flex flex-wrap gap-2 align-items-center my-3">
    <p-select
        [options]="this.dayOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="this.days"
        (onChange)="this.load()"
    ></p-select>
    <p-select
        [options]="this.providerOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="this.provider"
        (onChange)="this.load()"
    ></p-select>
    <p-select
        [options]="this.inputModeOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="this.inputMode"
        (onChange)="this.load()"
    ></p-select>
    <label class="d-flex align-items-center gap-2 ms-auto">
        <p-toggleswitch
            [ngModel]="this.showDetails()"
            (ngModelChange)="this.showDetails.set($event)"
        ></p-toggleswitch>
        <span i18n="@@latency-detail-toggle">Detailansicht</span>
    </label>
</div>

@if (this.summary(); as summary) {
@if (summary.count === 0) {
<p-message
    severity="secondary"
    [closable]="false"
>
    <span i18n="@@latency-empty">Für diesen Zeitraum gibt es noch keine Messwerte. Sprich mit dem Avatar, um die ersten
        Antwortzeiten zu erfassen.</span>
</p-message>
} @else {
<div class="row g-3 mb-3">
    <div class="col-12 col-md-6 col-xl-3">
        <p-card class="h-100 shadow-sm">
            <div class="text-muted small d-flex align-items-center gap-1">
                <span i18n="@@latency-kpi-perceived">Gefühlte Wartezeit</span>
                <i
                    class="ri-information-line"
                    pTooltip="Zeit vom Sprechende des Nutzers, bis der Avatar hörbar antwortet. Bei Spracheingabe inklusive der Pause, mit der das Sprechende erkannt wird. Entspricht dem, was Nutzer als Wartezeit erleben."
                    i18n-pTooltip="@@latency-kpi-perceived-info"
                    tooltipPosition="top"
                ></i>
            </div>
            <div class="fs-3 fw-bold">{{ this.seconds(summary.perceived?.p50) }}</div>
            <div class="small text-muted"><span i18n="@@latency-kpi-p90-prefix">90 % der Runden unter</span> {{
                this.seconds(summary.perceived?.p90) }}</div>
        </p-card>
    </div>
    <div class="col-12 col-md-6 col-xl-3">
        <p-card class="h-100 shadow-sm">
            <div class="text-muted small d-flex align-items-center gap-1">
                <span i18n="@@latency-kpi-system">Systemlatenz</span>
                <i
                    class="ri-information-line"
                    pTooltip="Zeit vom Absenden der Eingabe, bis der Avatar hörbar antwortet. Enthält Spracherkennung, KI-Antwort, Sprachausgabe und Avatar-Start, aber nicht die Pause zur Erkennung des Sprechendes."
                    i18n-pTooltip="@@latency-kpi-system-info"
                    tooltipPosition="top"
                ></i>
            </div>
            <div class="fs-3 fw-bold">{{ this.seconds(summary.system?.p50) }}</div>
            <div class="small text-muted"><span i18n="@@latency-kpi-p90-prefix">90 % der Runden unter</span> {{
                this.seconds(summary.system?.p90) }}</div>
        </p-card>
    </div>
    <div class="col-12 col-md-6 col-xl-3">
        <p-card class="h-100 shadow-sm">
            <div
                class="text-muted small"
                i18n="@@latency-kpi-under-3s"
            >Antworten unter 3 Sekunden</div>
            <div class="fs-3 fw-bold">{{ this.percent(summary.under3sShare) }}</div>
            <div
                class="small text-muted"
                i18n="@@latency-kpi-under-3s-hint"
            >bezogen auf die gefühlte Wartezeit</div>
        </p-card>
    </div>
    <div class="col-12 col-md-6 col-xl-3">
        <p-card class="h-100 shadow-sm">
            <div
                class="text-muted small"
                i18n="@@latency-kpi-count"
            >Gemessene Gesprächsrunden</div>
            <div class="fs-3 fw-bold">{{ summary.count }}</div>
        </p-card>
    </div>
</div>

<p-card class="mb-3 shadow-sm">
    <b i18n="@@latency-phases-title">Wo geht die Zeit hin?</b>
    <apx-chart
        [series]="this.phaseSeries()"
        [chart]="this.barChart"
        [plotOptions]="this.barPlotOptions"
        [xaxis]="this.barXAxis"
        [dataLabels]="this.barDataLabels"
        [tooltip]="this.secondsTooltip"
        [legend]="{ position: 'bottom' }"
    ></apx-chart>
    <p
        class="small text-muted mb-0"
        i18n="@@latency-phases-hint"
    >Die gefühlte Wartezeit zählt ab dem Moment, in dem der Nutzer aufhört zu sprechen, die Systemlatenz ab dem Absenden
        der Eingabe. Jede Phase zeigt ihren eigenen Median bzw. 90-%-Wert, deshalb kann die Summe leicht von der
        Gesamtzeit abweichen.</p>
</p-card>

@if (this.showDetails()) {
<p-card class="mb-3 shadow-sm">
    <b i18n="@@latency-scatter-title">Antwortlänge und Wartezeit</b>
    <apx-chart
        [series]="this.scatterSeries()"
        [chart]="this.scatterChart"
        [xaxis]="this.scatterXAxis"
        [yaxis]="this.scatterYAxis"
        [tooltip]="this.secondsTooltip"
    ></apx-chart>
    <p
        class="small text-muted mb-0"
        i18n="@@latency-scatter-hint"
    >Die Sprachausgabe startet erst, wenn die KI-Antwort vollständig ist. Lange Antworten verlängern die Wartezeit daher
        direkt.</p>
</p-card>

<p-card class="mb-3 shadow-sm">
    <div class="d-flex align-items-center justify-content-between">
        <b i18n="@@latency-turns-title">Letzte Gesprächsrunden</b>
        <p-button
            label="Exportieren"
            i18n-label="@@latency-export"
            pTooltip="Alle Gesprächsrunden des gewählten Zeitraums und Filters als Excel-Datei herunterladen"
            i18n-pTooltip="@@latency-export-info"
            tooltipPosition="left"
            icon="ri-download-line"
            severity="secondary"
            size="small"
            [outlined]="true"
            (onClick)="this.exportTurns()"
        ></p-button>
    </div>
    <p-table
        [value]="this.recentTurns()"
        dataKey="turnId"
        class="p-datatable-sm mt-2"
    >
        <ng-template #header>
            <tr>
                <th style="width: 2.5rem"></th>
                <th i18n="@@latency-col-time">Zeitpunkt</th>
                <th i18n="@@latency-col-provider">Avatar</th>
                <th i18n="@@latency-col-input">Eingabe</th>
                <th i18n="@@latency-col-perceived">Gefühlt</th>
                <th i18n="@@latency-col-system">System</th>
                <th
                    style="min-width: 10rem"
                    i18n="@@latency-col-phases"
                >Phasen</th>
                <th i18n="@@latency-col-chars">Zeichen</th>
                <th i18n="@@latency-col-tools">Tool-Runden</th>
                <th i18n="@@latency-col-docsearch">Dokumentsuche</th>
                <th i18n="@@latency-col-model">Modell</th>
                <th i18n="@@latency-col-tts">TTS-Modell</th>
            </tr>
        </ng-template>
        <ng-template
            #body
            let-turn
            let-expanded="expanded"
        >
            <tr>
                <td>
                    <button
                        type="button"
                        class="btn btn-sm btn-link p-0 text-body"
                        [pRowToggler]="turn"
                        [attr.aria-expanded]="expanded"
                        aria-label="Phasen anzeigen"
                        i18n-aria-label="@@latency-row-toggle"
                    >
                        <i [class]="expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                    </button>
                </td>
                <td>{{ turn.created | date: 'short' }}</td>
                <td>{{ this.providerLabel(turn.provider) }}</td>
                <td>{{ this.inputModeLabel(turn.inputMode) }}</td>
                <td>{{ this.seconds(turn.perceivedMs) }}</td>
                <td>{{ this.seconds(turn.systemMs) }}</td>
                <td>
                    <div
                        class="d-flex rounded overflow-hidden"
                        style="height: 0.75rem"
                    >
                        @for (key of this.detailPhases; track key) {
                        @if (turn.detail[key] > 0) {
                        <div
                            [style.width.%]="this.segmentWidth(turn, key)"
                            [style.background-color]="this.phaseColors[key]"
                            [pTooltip]="this.phaseTooltip(turn, key)"
                            tooltipPosition="top"
                        ></div>
                        }
                        }
                    </div>
                </td>
                <td>{{ turn.answerChars ?? '–' }}</td>
                <td>{{ turn.toolRounds ?? 0 }}</td>
                <td>
                    @if (turn.docSearchUsed) {
                    <i class="ri-check-line"></i>
                    }
                </td>
                <td>{{ turn.llmModel ?? '–' }}</td>
                <td>{{ turn.ttsModel ?? '–' }}</td>
            </tr>
        </ng-template>
        <ng-template
            #expandedrow
            let-turn
        >
            <tr>
                <td></td>
                <td colspan="11">
                    <div class="d-flex flex-wrap gap-3 py-1">
                        @for (key of this.detailPhases; track key) {
                        <div class="d-flex align-items-center gap-1 small">
                            <span
                                class="d-inline-block rounded-circle"
                                style="width: 0.6rem; height: 0.6rem"
                                [style.background-color]="this.phaseColors[key]"
                            ></span>
                            <span class="text-muted">{{ this.phaseLabels[key] }}:</span>
                            <b>{{ this.seconds(turn.detail[key]) }}</b>
                        </div>
                        }
                    </div>
                </td>
            </tr>
        </ng-template>
    </p-table>
</p-card>
}
}
}